import { SearchableSelect } from "../../components/ui/searchable-select";
import { MultiSelect } from "../../components/ui/multi-select";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import {
  ArrowLeft, Edit, Trash2, User as UserIcon, Calendar, Clock,
  Link2, Monitor, Archive, ArchiveRestore, AlertCircle, Download, AlertTriangle,
  Building2, FileText, QrCode, BookOpen, HelpCircle, CheckCircle, Layers,
  Video, ExternalLink, Pencil, GripVertical, Plus, X, ChevronRight,
  ClipboardList, ArrowUpDown
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Procedure, ProcedureLink, ActivityLogEntry, System, User, Activity, ActivityRaci, RaciRole, PartyType, RaciParty, ExternalPartner } from "../../types/index";
import { canUserEditProcedure, canUserArchiveProcedure, canArchiveProcedure, canDeleteProcedure, getTrainingPlansUsingProcedure, userHasAccessToDepartment, canUserViewProcedure } from "../../utils/procedure-permissions";
import jsPDF from 'jspdf';
import { debounce } from 'lodash';
import FileAttachments from "../../components/FileAttachments";
import ProcedureQRModal from "../../components/Procedures/ProcedureQRModal";
import { ProcedurePageSkeleton } from "../../components/Procedures/ProcedurePageSkeleton";
import SharePointVideoPlayer from "../../components/Procedures/SharePointVideoPlayer";
import { isArrayOfStrings, formatArchiveDate, getDepartmentIdByUser, toSydneyDate } from "../../utils/helpers";
import { Can } from "../../components/Can";
import { CanAny } from "../../components/CanAny";
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ActivityLog } from "../../components/ActivityLog";
import api from "../../utils/api";
import { cn } from "../../components/ui/utils";
import CustomEditor from "../../components/ui/custom-text-editor";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ProcedureStep {
  id: string;
  number: number;
  title: string;
  content: string;
}

interface NewActivity {
  activityName: string;
  description: string;
  procedure: string;
  sequence: number;
  assignedTo: string;
  status?: string;
  dependencies?: Array<{ activity: string; type: string }>;
  dueDate?: string | undefined;
  responsiblePerson: string;
  accountablePerson: string;
  consultedPerson?: string | undefined;
  informedPerson?: string | undefined;
  createdBy?: string;
  createdAt?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const getProcedure = async (id: any) => {
  const data = await api.get(`/api/procedures/${id}`);
  return data.procedure || null;
}

/** Parse howToComplete into steps, or return empty array (legacy backward compat) */
const parseSteps = (howToComplete: string | undefined): ProcedureStep[] => {
  if (!howToComplete) return [];
  try {
    const parsed = JSON.parse(howToComplete);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* not JSON, treat as legacy rich-text */ }
  return [];
};

/** Generate default steps from legacy HTML content (best-effort) */
const generateDefaultSteps = (htmlContent: string | undefined): ProcedureStep[] => {
  if (!htmlContent) return [];
  // Try to extract list items or paragraphs as steps
  const temp = document.createElement('div');
  temp.innerHTML = htmlContent;
  const items = temp.querySelectorAll('li, p');
  if (items.length === 0) {
    // Fallback: treat entire content as one step
    return [{ id: `step-${Date.now()}`, number: 1, title: 'Step 1', content: temp.textContent || '' }];
  }
  return Array.from(items).map((item, i) => ({
    id: `step-${Date.now()}-${i}`,
    number: i + 1,
    title: `Step ${i + 1}`,
    content: item.textContent || '',
  }));
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NewProcedurePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    departments, users, procedures, systems,
    updateProcedure, deleteProcedureLink,
    addActivity, updateActivity, deleteActivity,
    archiveProcedure, unarchiveProcedure,
    trainingPlans, jobRoles, externalPartners
  } = useData();
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);

  // Filter departmental users for task assignment
  const departmentalUsers = users;

  /* -------------------- State -------------------- */
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [hasProcedureLoaded, setHasProcedureLoaded] = useState(false);
  const [description, setDescription] = useState("");
  const [whyImportant, setWhyImportant] = useState("");
  const [procedureActivities, setProcedureActivities] = useState<Activity[]>([]);
  const [activeTab, setActiveTab] = useState<'procedure' | 'activity' | 'activity_log'>('procedure');
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Edit states
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingWhyImportant, setEditingWhyImportant] = useState(false);
  const [editingHowTo, setEditingHowTo] = useState(false);
  const [isSavingHowTo, setIsSavingHowTo] = useState(false);

  // Steps state
  const [steps, setSteps] = useState<ProcedureStep[]>([]);
  const [stepsDraft, setStepsDraft] = useState<ProcedureStep[]>([]);

  // Systems
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [editSystems, setEditSystems] = useState(false);
  const [isSavingSystems, setIsSavingSystems] = useState(false);

  // Links
  const [openAddLink, setOpenAddLink] = useState(false);
  const [newLink, setNewLink] = useState<{ title: string; url: string; description: string }>({ title: "", url: "", description: "" });
  const [openEditLink, setOpenEditLink] = useState(false);
  const [editingLink, setEditingLink] = useState<ProcedureLink | null>(null);

  // Archive / Delete
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [openUnarchiveDialog, setOpenUnarchiveDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [qrModalOpen, setQrModalOpen] = useState(false);

  // Attachments
  const [procedureAttachments, setProcedureAttachments] = useState<any[]>([]);

  // Activity dialogs
  const [openAddActivity, setOpenAddActivity] = useState(false);
  const [openEditActivity, setOpenEditActivity] = useState(false);
  const [openDeleteActivity, setOpenDeleteActivity] = useState(false);
  const [editingActivity, setEditingActivity] = useState<{ id: string; activityName: string; raci: ActivityRaci | undefined } | null>(null);
  const [deleteActivityId, setDeleteActivityId] = useState("");
  const [newActivityName, setNewActivityName] = useState("");
  const [raci, setRaci] = useState<ActivityRaci | undefined>({});
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);

  // Activity table sort
  const [activitySort, setActivitySort] = useState<{ column: 'activityName' | 'responsible' | 'accountable' | 'consulted' | 'informed' | 'createdAt'; direction: 'asc' | 'desc' }>({ column: 'activityName', direction: 'asc' });

  // Active section in left sidebar
  const [activeSection, setActiveSection] = useState<string>('what');

  /* -------------------- Effects -------------------- */

  useEffect(() => {
    const fetchProcedure = async () => {
      if (!id) return;
      setHasProcedureLoaded(false);
      try {
        const found = await getProcedure(id);
        if (found) {
          setProcedure(found);
          setDescription(found.description || "");
          setWhyImportant(found.whyImportant || "");
          setSelectedSystems(found.systems?.map((s: System) => typeof s !== 'string' ? s.name : "") || []);
          setProcedureAttachments(found.attachments || []);
          setProcedureActivities(found.activities || []);

          // Load steps from new procedure_steps table (or fallback to legacy howToComplete)
          if (found.steps && found.steps.length > 0) {
            setSteps(found.steps.map((s: any, i: number) => ({
              id: String(s.id),
              number: s.sequence || i + 1,
              title: s.title || `Step ${i + 1}`,
              content: s.content || '',
            })));
          } else if (found.howToComplete) {
            // Legacy content - parse JSON or generate from HTML
            const parsed = parseSteps(found.howToComplete);
            if (parsed.length > 0) {
              setSteps(parsed);
            } else {
              const generated = generateDefaultSteps(found.howToComplete);
              setSteps(generated);
            }
          } else {
            setSteps([]);
          }
        }
      } catch (err) {
        console.error("Error fetching procedure:", err);
      } finally {
        setHasProcedureLoaded(true);
      }
    };
    fetchProcedure();
  }, [id, procedures]);

  useEffect(() => {
    if (procedure?.whyImportant !== undefined) setWhyImportant(procedure.whyImportant);
  }, [procedure?.whyImportant]);

  useEffect(() => {
    if (procedure?.description) setDescription(procedure.description);
  }, [procedure?.description]);

  useEffect(() => {
    if (editingActivity?.raci) setRaci(editingActivity.raci);
    else setRaci({});
  }, [editingActivity]);

  useEffect(() => {
    if (activeTab === 'activity_log') fetchActivityLogs();
  }, [activeTab, id]);

  // Scroll spy for left sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['what', 'why', 'how', 'systems', 'links', 'attachments'];
      for (const section of sections) {
        const el = document.getElementById(`section-${section}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 100 && rect.top <= 300) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* -------------------- Computed -------------------- */

  const canEdit = currentUser?.userRole === "Admin" ||
    currentUser?.userRole === "Director" ||
    (currentUser?.userRole === "Manager" && procedure?.department?.id == getDepartmentIdByUser(currentUser)) ||
    (currentUser?.jobRole === "HSEQ Manager");

  const canDelete = currentUser?.userRole === "Admin" ||
    currentUser?.userRole === "Director" ||
    currentUser?.jobRole === "HSEQ Manager";

  const canArchive = currentUser && procedure ? canUserArchiveProcedure(currentUser, procedure) : false;

  const stakeholders = useMemo(() => {
    return procedure?.procedureStakeholders
      ? procedure.procedureStakeholders
        .map(s => typeof s === 'string' ? users.find(u => u.id === s) : s)
        .filter((u): u is User => !!u)
      : [];
  }, [procedure?.procedureStakeholders, users]);

  const viewers = useMemo(() => {
    return procedure?.viewers
      ? procedure.viewers
        .map(v => typeof v === 'string' ? users.find(u => u.id === v) : v)
        .filter((u): u is User => !!u)
      : [];
  }, [procedure?.viewers, users]);

  const allSystemsFormatted = useMemo(() => {
    return systems
      ?.map(s => ({ value: s.name, label: s.active ? s.name : `${s.name} (Inactive)`, isInactive: !s.active }))
      .sort((a, b) => {
        if (a.isInactive !== b.isInactive) return a.isInactive ? 1 : -1;
        return a.label.localeCompare(b.label);
      }) || [];
  }, [systems]);

  const owner = typeof procedure?.procedureOwner === 'string'
    ? users.find(u => u.id === procedure.procedureOwner)
    : procedure?.procedureOwner;

  const department = procedure?.department;
  const creator = typeof procedure?.createdBy === 'string'
    ? users.find(u => u.id === procedure.createdBy)
    : procedure?.createdBy;
  const archiver = procedure?.archivedBy
    ? (typeof procedure.archivedBy === 'string' ? users.find(u => u.id == procedure.archivedBy) : procedure.archivedBy)
    : null;

  /* -------------------- Handlers -------------------- */

  const fetchActivityLogs = async () => {
    if (!id) return;
    setIsLoadingLogs(true);
    try {
      const data = await api.get(`/api/procedures/${id}/activity-logs`);
      if (data.success) setActivityLogs(data.activityLogs);
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      toast.error("Failed to load activity logs");
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleDescriptionSave = async () => {
    if (!procedure) return;
    try {
      await updateProcedure(procedure.id, { description }, true);
      setEditingDescription(false);
      setProcedure(prev => prev ? { ...prev, description } : null);
    } catch (err) {
      console.error("Failed to save description:", err);
      toast.error("Failed to save changes");
    }
  };

  const handleWhyImportantSave = async () => {
    if (!procedure) return;
    try {
      await updateProcedure(procedure.id, { whyImportant }, true);
      setEditingWhyImportant(false);
      setProcedure(prev => prev ? { ...prev, whyImportant } : null);
    } catch (err) {
      console.error("Failed to save why important:", err);
      toast.error("Failed to save changes");
    }
  };

  const handleHowToSave = async () => {
    if (!procedure) return;
    setIsSavingHowTo(true);
    try {
      // Sync steps to the new procedure_steps API
      // Strategy: delete all existing steps, then recreate from draft
      const stepRepository = {
        getAll: async () => api.get(`/api/procedures/${procedure.id}/steps`),
        create: async (data: any) => api.post(`/api/procedures/${procedure.id}/steps`, data),
        delete: async (stepId: string) => api.delete(`/api/procedures/${procedure.id}/steps/${stepId}`),
        reorder: async (stepIds: string[]) => api.put(`/api/procedures/${procedure.id}/steps/reorder`, { stepIds }),
      };

      // Get current steps
      const currentStepsRes = await stepRepository.getAll();
      const currentSteps = currentStepsRes.success ? currentStepsRes.steps : [];

      // Delete all existing steps (we'll recreate them)
      for (const step of currentSteps) {
        await stepRepository.delete(step.id);
      }

      // Create new steps from draft
      const createdSteps: ProcedureStep[] = [];
      for (let i = 0; i < stepsDraft.length; i++) {
        const draft = stepsDraft[i];
        const created = await stepRepository.create({
          title: draft.title || `Step ${i + 1}`,
          content: draft.content || '',
        });
        if (created.success) {
          createdSteps.push({
            id: String(created.step.id),
            number: created.step.sequence,
            title: created.step.title,
            content: created.step.content,
          });
        }
      }

      setEditingHowTo(false);
      setSteps(createdSteps);
      
      // Bump the procedure version by calling updateProcedure with a no-op change
      // The backend increments currentVersion on every PUT
      try {
        await updateProcedure(procedure.id, { howToComplete: procedure.howToComplete || '' }, false);
      } catch (versionErr) {
        console.error('Failed to bump version:', versionErr);
      }
      
      setProcedure(prev => prev ? { ...prev, steps: createdSteps, currentVersion: (prev.currentVersion || 1) + 1 } : null);
      toast.success("Steps saved successfully");
    } catch (err) {
      console.error("Failed to save steps:", err);
      toast.error("Failed to save changes");
    } finally {
      setIsSavingHowTo(false);
    }
  };

  const handleSystemsUpdate = async () => {
    if (!procedure) return;
    setIsSavingSystems(true);
    try {
      await updateProcedure(procedure.id, { systems: selectedSystems }, true);
      setProcedure(prev => prev ? { ...prev, systems: selectedSystems } : null);
      setEditSystems(false);
    } catch (err) {
      console.error("Failed to save systems:", err);
      toast.error("Failed to save systems");
    } finally {
      setIsSavingSystems(false);
    }
  };

  const handleAddLink = () => {
    if (!newLink.title || !newLink.url) {
      toast.error("Link title and URL are required");
      return;
    }
    const linkEntry: ProcedureLink = { id: Date.now().toString(), title: newLink.title, url: newLink.url, description: newLink.description };
    const updatedLinks = procedure?.links ? [...procedure.links, linkEntry] : [linkEntry];
    updateProcedure(procedure!.id, { links: updatedLinks.map(l => ({ id: l.id, title: l.title, url: l.url, description: l.description || "" })) }, true);
    setProcedure(prev => prev ? { ...prev, links: updatedLinks } : null);
    setNewLink({ title: "", url: "", description: "" });
    setOpenAddLink(false);
  };

  const handleRemoveLink = (linkId: string) => {
    if (!procedure) return;
    const updatedLinks = procedure.links?.filter(l => l.id != linkId) || [];
    deleteProcedureLink(procedure.id, linkId);
    updateProcedure(procedure.id, {}, false);
    setProcedure(prev => prev ? { ...prev, links: updatedLinks } : null);
  };

  const handleEditLink = () => {
    if (!procedure || !editingLink) return;
    const updatedLinks = procedure.links?.map(l => l.id === editingLink.id ? { ...editingLink, description: editingLink.description || "" } : l) || [];
    updateProcedure(procedure.id, { links: updatedLinks.map(l => ({ id: l.id, title: l.title, url: l.url, description: l.description || "" })) }, true);
    setEditingLink(null);
    setOpenEditLink(false);
    setProcedure(prev => prev ? { ...prev, links: updatedLinks } : null);
  };

  const handleArchive = () => {
    if (!currentUser || !procedure) return;
    archiveProcedure(procedure.id, archiveReason);
    setOpenArchiveDialog(false);
    setArchiveReason("");
  };

  const handleUnarchive = () => {
    if (!currentUser || !procedure) return;
    unarchiveProcedure(procedure.id, currentUser.id);
    setOpenUnarchiveDialog(false);
    setProcedure(prev => prev ? { ...prev, archived: false, archivedOn: undefined, archivedBy: undefined, archivedReason: undefined } : null);
  };

  const handleDelete = () => {
    if (!currentUser || !procedure || deleteConfirmation !== "delete") return;
    const { canDelete: canDel } = canDeleteProcedure(procedure, trainingPlans);
    if (!canDel) {
      setOpenDeleteDialog(false);
      setDeleteConfirmation("");
      return;
    }
    updateProcedure(procedure.id, { deleted: true, deletedBy: currentUser.id }, false);
    setDeleteConfirmation("");
    setOpenDeleteDialog(false);
    toast.success("Procedure deleted successfully");
    navigate("/procedure");
  };

  const handleDownloadPDF = () => {
    if (!procedure) return;
    const doc = new jsPDF();
    let yPos = 20;
    doc.setFontSize(20);
    doc.text(procedure.procedureName, 20, yPos);
    yPos += 15;
    doc.setFontSize(12);
    doc.text(`Created by: ${creator ? creator.name : 'Unknown'}`, 20, yPos);
    yPos += 10;
    doc.text(`Created on: ${toSydneyDate(procedure.createdAt)}`, 20, yPos);
    yPos += 10;
    doc.text(`Department: ${department ? `${department.departmentCode} - ${department.departmentName}` : 'Unknown'}`, 20, yPos);
    yPos += 15;

    if (procedure.systems && procedure.systems.length > 0) {
      doc.setFontSize(14);
      doc.text('Systems Used:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      !isArrayOfStrings(procedure.systems) && procedure.systems.forEach((s: System) => {
        doc.text(`• ${s.name}`, 25, yPos);
        yPos += 7;
      });
      yPos += 5;
    }

    if (procedure.links && procedure.links.length > 0) {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(14);
      doc.text('Links:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      procedure.links.forEach((l: ProcedureLink) => {
        doc.text(`• ${l.title}`, 25, yPos);
        yPos += 7;
        doc.setTextColor(0, 0, 255);
        doc.text(`  ${l.url}`, 30, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 7;
      });
      yPos += 5;
    }

    if (procedure.description) {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(14);
      doc.text('What is this procedure:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const split = doc.splitTextToSize(procedure.description, 170);
      doc.text(split, 20, yPos);
      yPos += split.length * 7 + 10;
    }

    if (procedure.whyImportant) {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(14);
      doc.text('Why is this important:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const split = doc.splitTextToSize(procedure.whyImportant, 170);
      doc.text(split, 20, yPos);
      yPos += split.length * 7 + 10;
    }

    if (steps.length > 0) {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(14);
      doc.text('How to complete:', 20, yPos);
      yPos += 10;
      steps.forEach((step) => {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFontSize(12);
        doc.text(`${step.number}. ${step.title}`, 25, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const split = doc.splitTextToSize(step.content, 160);
        doc.text(split, 30, yPos);
        yPos += split.length * 7 + 5;
      });
    }

    doc.save(`${procedure.procedureName.replace(/\s+/g, '_')}.pdf`);
  };

  /* -------------------- Step Builder Handlers -------------------- */

  const startEditingHowTo = () => {
    setStepsDraft([...steps]);
    setEditingHowTo(true);
  };

  const addStep = () => {
    const newStep: ProcedureStep = {
      id: `step-${Date.now()}`,
      number: stepsDraft.length + 1,
      title: '',
      content: '',
    };
    setStepsDraft([...stepsDraft, newStep]);
  };

  const updateStep = (stepId: string, field: 'title' | 'content', value: string) => {
    setStepsDraft(prev => prev.map(s => s.id === stepId ? { ...s, [field]: value } : s));
  };

  const removeStep = (stepId: string) => {
    setStepsDraft(prev => {
      const filtered = prev.filter(s => s.id !== stepId);
      return filtered.map((s, i) => ({ ...s, number: i + 1 }));
    });
  };

  const moveStepUp = (index: number) => {
    if (index === 0) return;
    setStepsDraft(prev => {
      const newSteps = [...prev];
      [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
      return newSteps.map((s, i) => ({ ...s, number: i + 1 }));
    });
  };

  const moveStepDown = (index: number) => {
    if (index === stepsDraft.length - 1) return;
    setStepsDraft(prev => {
      const newSteps = [...prev];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      return newSteps.map((s, i) => ({ ...s, number: i + 1 }));
    });
  };

  /* -------------------- Activity Handlers -------------------- */

  const handleAddActivity = async () => {
    if (!newActivityName) { toast.error("Task name is required."); return; }
    if (!raci?.responsiblePerson || !raci.accountablePerson) { toast.error("A Responsible and an Accountable person/role are required."); return; }
    if (!procedure?.id) { toast.error("Procedure context is missing."); return; }
    const sequence = procedureActivities.length > 0 ? Math.max(...procedureActivities.map(a => a.sequence || 0)) + 1 : 0;
    try {
      const created = await addActivity({ activityName: newActivityName, raci, procedureId: procedure.id, sequence });
      if (created) {
        toast.success("Task added successfully!");
        setProcedureActivities(prev => [...prev, { ...created, procedure: procedure.id, raci }]);
        setOpenAddActivity(false);
        setRaci({});
        setNewActivityName('');
      } else {
        toast.error("Failed to add task.");
      }
    } catch (err) {
      console.error("Error adding task:", err);
      toast.error("An unexpected error occurred.");
    }
  };

  const handleRaciSelect = (role: RaciRole, value: string | string[]) => {
    if (role === 'consultedPerson' || role === 'informedPerson') {
      if (Array.isArray(value)) {
        const parties: RaciParty[] = value.map(v => { const [type, id] = v.split('-'); return { id, type: type as PartyType }; });
        setRaci(current => ({ ...current, [role]: parties }));
      }
      return;
    }
    if (typeof value === 'string') {
      const [type, id] = value.split('-');
      if (id === 'none') {
        setRaci(current => { const { [role]: removed, ...rest } = current || {}; return rest; });
        return;
      }
      setRaci(current => ({ ...current, [role]: { id, type: type as PartyType } }));
    }
  };

  const handleUpdateActivity = async () => {
    if (!editingActivity || !editingActivity.activityName || !raci?.responsiblePerson || !raci?.accountablePerson) {
      toast.error("Task name, responsible person, and accountable person are required");
      return;
    }
    if (!procedure?.id) { toast.error("Procedure not found."); return; }
    try {
      const response = await updateActivity(procedure.id, editingActivity.id, { activityName: editingActivity.activityName, raci });
      if (response.success) setProcedureActivities(response.activities);
      setEditingActivity(null);
      setOpenEditActivity(false);
      toast.success("Task updated successfully");
    } catch (err) {
      console.error("Error updating task:", err);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteActivity = async () => {
    setIsDeletingActivity(true);
    try {
      const toDelete = procedureActivities.find(a => a.id == deleteActivityId);
      if (!toDelete) return;
      await updateProcedure(procedure!.id, {}, false);
      await deleteActivity(procedure!.id, deleteActivityId);
      setProcedureActivities(prev => prev.filter(a => a.id !== deleteActivityId));
      setDeleteActivityId("");
      setOpenDeleteActivity(false);
      toast.success("Activity deleted successfully");
    } catch (err) {
      console.error("Error deleting activity:", err);
      toast.error("Failed to delete activity");
    } finally {
      setIsDeletingActivity(false);
    }
  };

  const moveActivityUp = async (currentIndex: number) => {
    if (currentIndex === 0) return;
    try {
      const newActivities = [...procedureActivities];
      const current = newActivities[currentIndex];
      const previous = newActivities[currentIndex - 1];
      if (!current || !previous) return;
      const optimistic = [...newActivities];
      [optimistic[currentIndex], optimistic[currentIndex - 1]] = [optimistic[currentIndex - 1], optimistic[currentIndex]];
      const tempSeq = optimistic[currentIndex].sequence;
      optimistic[currentIndex].sequence = optimistic[currentIndex - 1].sequence;
      optimistic[currentIndex - 1].sequence = tempSeq;
      setProcedureActivities(optimistic);
      await updateActivity(procedure!.id, current.id, { sequence: previous.sequence, _swapWithActivityId: previous.id }, false);
    } catch (err) {
      console.error('Error moving activity up:', err);
      toast.error('Failed to reorder activities');
    }
  };

  const moveActivityDown = async (currentIndex: number) => {
    if (currentIndex === procedureActivities.length - 1) return;
    try {
      const newActivities = [...procedureActivities];
      const current = newActivities[currentIndex];
      const next = newActivities[currentIndex + 1];
      if (!current || !next) return;
      const optimistic = [...newActivities];
      [optimistic[currentIndex], optimistic[currentIndex + 1]] = [optimistic[currentIndex + 1], optimistic[currentIndex]];
      const tempSeq = optimistic[currentIndex].sequence;
      optimistic[currentIndex].sequence = optimistic[currentIndex + 1].sequence;
      optimistic[currentIndex + 1].sequence = tempSeq;
      setProcedureActivities(optimistic);
      await updateActivity(procedure!.id, current.id, { sequence: next.sequence, _swapWithActivityId: next.id }, false);
    } catch (err) {
      console.error('Error moving activity down:', err);
      toast.error('Failed to reorder activities');
    }
  };

  const getPartyName = (party: RaciParty): string => {
    if (!party || !party.id) return "Invalid Party";
    switch (party.type) {
      case PartyType.User: {
        const user = users.find(u => u.id == party.id);
        if (user) return user.name;
        if ('name' in party && (party as any).name) return (party as any).name;
        return "Unknown User";
      }
      case PartyType.JobRole: {
        const role = jobRoles.find(r => r.id == party.id);
        return role ? `${role.title} (Role)` : "Unknown Role";
      }
      case PartyType.ExternalPartner: {
        const partner = externalPartners.find(p => p.id == party.id);
        return partner ? `${partner.name} (External)` : "Unknown Partner";
      }
      default: return "Unknown Party Type";
    }
  };

  const refetchProcedure = async () => {
    if (!id) return;
    try {
      setHasProcedureLoaded(false);
      const data = await api.get(`/api/procedures/${id}`);
      if (data.success && data.procedure) {
        setProcedure(data.procedure);
        setProcedureAttachments(data.procedure.attachments || []);
      }
      setHasProcedureLoaded(true);
    } catch (err) {
      console.error('Error refetching procedure:', err);
      setHasProcedureLoaded(true);
    }
  };

  const handleAttachmentsChange = async (newAttachments: any[]) => {
    setProcedureAttachments(newAttachments);
    if (procedure) setProcedure({ ...procedure, attachments: newAttachments });
    setTimeout(() => refetchProcedure(), 500);
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  /* -------------------- Render Guards -------------------- */

  if (!hasProcedureLoaded) return <ProcedurePageSkeleton />;
  if (!procedure) return <div className="flex items-center justify-center h-full"><p>Procedure not found</p></div>;
  if (!canUserViewProcedure(currentUser, procedure, ability)) return <div className="flex items-center justify-center h-full"><p>You don't have permission to access this procedure</p></div>;

  /* -------------------- Render -------------------- */

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2 text-lg sm:text-2xl font-bold">
              <span className="truncate">{procedure.procedureName}</span>
              <Badge variant="outline" className="bg-blue-100 border-blue-200 text-blue-800 hover:bg-blue-100 flex-shrink-0 text-xs">
                Procedure
              </Badge>
              {procedure.archived && (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 flex-shrink-0 text-xs">
                  <Archive className="w-3 h-3 mr-1" /> Archived
                </Badge>
              )}
            </h1>
          </div>
        </div>

        <div className="hidden lg:flex gap-2 flex-shrink-0">
          {!procedure.archived && (
            <>
              <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_I_OWN }]}>
                <Link to={`/procedure/${procedure.id}/edit`}>
                  <Button variant="outline"><Edit className="w-4 h-4 mr-2" />Edit</Button>
                </Link>
              </CanAny>
              <Can I={ACTIONS.DOWNLOAD} a={RESOURCES.PROCEDURE_PDF}>
                <Button variant="outline" onClick={handleDownloadPDF}><Download className="w-4 h-4 mr-2" />PDF</Button>
              </Can>
              <Button variant="outline" onClick={() => setQrModalOpen(true)}><QrCode className="w-4 h-4 mr-2" />QR</Button>
            </>
          )}
          {!procedure.archived && (
            <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PROCEDURE}>
              <Dialog open={openArchiveDialog} onOpenChange={(open) => { setOpenArchiveDialog(open); if (!open) setArchiveReason(""); }}>
                <DialogTrigger asChild><Button variant="outline"><Archive className="w-4 h-4 mr-2" />Archive</Button></DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader><DialogTitle className="text-base font-medium">Archive Procedure</DialogTitle></DialogHeader>
                  {(() => {
                    const { canArchive: canArch, activePlans } = canArchiveProcedure(procedure, trainingPlans);
                    if (!canArch) {
                      return (
                        <div className="space-y-4">
                          <div className="bg-[#FEF9C3] border border-[#F7E9A8] rounded-lg p-4">
                            <div className="flex gap-2">
                              <AlertTriangle className="w-5 h-5 text-[#854D0E]" />
                              <div className="text-[#854D0E] text-sm">
                                <p>This procedure cannot be archived because it's currently used in active training plans:</p>
                                <ul className="list-disc list-inside mt-2">
                                  {activePlans.map((plan: any) => (
                                    <li key={plan.id}>{getPartyName(plan.traineeId)} – {plan.trainingPlanName} – {plan.status}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                          <DialogFooter><DialogClose asChild><Button variant="outline">Close</Button></DialogClose></DialogFooter>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Are you sure you want to archive "{procedure.procedureName}"?</p>
                        <div className="space-y-2"><Label htmlFor="archiveReason">Reason (Optional)</Label><Textarea id="archiveReason" value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} className="resize-none" /></div>
                        <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleArchive}>Archive</Button></DialogFooter>
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            </Can>
          )}
          {procedure.archived && (
            <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PROCEDURE}>
              <Dialog open={openUnarchiveDialog} onOpenChange={setOpenUnarchiveDialog}>
                <DialogTrigger asChild><Button variant="outline"><ArchiveRestore className="w-4 h-4 mr-2" />Unarchive</Button></DialogTrigger>
                <DialogContent aria-describedby={undefined}><DialogHeader><DialogTitle>Unarchive Procedure</DialogTitle><DialogDescription>This will restore the procedure to an active state.</DialogDescription></DialogHeader><DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleUnarchive}>Unarchive</Button></DialogFooter></DialogContent>
              </Dialog>
            </Can>
          )}
          {!procedure.archived && (
            <Can I={ACTIONS.DELETE} a={RESOURCES.PROCEDURE}>
              <Dialog open={openDeleteDialog} onOpenChange={(open) => { if (open) { setOpenDeleteDialog(true); setDeleteConfirmation(""); } else { setOpenDeleteDialog(false); setDeleteConfirmation(""); } }}>
                <DialogTrigger asChild><Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-100 hover:text-red-700"><Trash2 className="w-4 h-4 mr-2" />Delete</Button></DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader><DialogTitle>Delete Procedure</DialogTitle></DialogHeader>
                  {(() => {
                    const { canDelete: canDel } = canDeleteProcedure(procedure, trainingPlans);
                    if (!canDel) {
                      return (
                        <div className="space-y-4 py-2">
                          <p className="text-destructive font-medium">This procedure cannot be deleted due to linked training plans.</p>
                          <p className="text-sm text-muted-foreground">You can archive it to keep a historical record.</p>
                          <DialogFooter><DialogClose asChild><Button variant="outline">Okay</Button></DialogClose></DialogFooter>
                        </div>
                      );
                    }
                    return (
                      <>
                        <div className="py-4">
                          <p>Are you sure you want to delete "{procedure.procedureName}"?</p>
                          <p className="text-sm text-muted-foreground mt-2">Type "delete" to confirm.</p>
                          <div className="mt-4"><Label htmlFor="deleteConfirmation">Confirmation</Label><Input id="deleteConfirmation" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} placeholder="delete" className="mt-1.5" /></div>
                        </div>
                        <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button variant="destructive" onClick={handleDelete} disabled={deleteConfirmation !== "delete"}>Delete</Button></DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            </Can>
          )}
        </div>
      </div>

      {/* Archive warning */}
      {procedure.archived && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-yellow-600" />
              <div>
                <h3 className="font-medium text-yellow-800">This procedure has been archived</h3>
                <p className="text-sm text-yellow-700">Archived on {formatArchiveDate(procedure.archivedOn || "")} by {archiver ? archiver.name : "Unknown"}{procedure.archivedReason && <>. Reason: {procedure.archivedReason}</>}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'procedure' | 'activity' | 'activity_log')} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-full sm:w-fit mb-4">
          <TabsTrigger value="procedure" className="rounded-full px-3 sm:px-4 py-2 text-sm data-[state=active]:bg-white flex-1 sm:flex-none">Procedure</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-full px-3 sm:px-4 py-2 text-sm data-[state=active]:bg-white flex-1 sm:flex-none">Tasks</TabsTrigger>
          <TabsTrigger value="activity_log" className="rounded-full px-3 sm:px-4 py-2 text-sm data-[state=active]:bg-white flex-1 sm:flex-none">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="procedure" className="space-y-6">
          {/* Three-column layout: Left sidebar | Center content | Right sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Sidebar - Contents */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="sticky top-4 space-y-4">
                <div className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Contents</div>
                <nav className="space-y-1">
                  {[
                    { id: 'what', label: 'What is this procedure', icon: <BookOpen size={14} /> },
                    { id: 'why', label: "Why it's important", icon: <HelpCircle size={14} /> },
                    { id: 'how', label: 'How to complete', icon: <CheckCircle size={14} /> },
                    { id: 'systems', label: 'Systems used', icon: <Layers size={14} /> },
                    { id: 'links', label: 'Links & resources', icon: <Link2 size={14} /> },
                    { id: 'attachments', label: 'Attachments', icon: <FileText size={14} /> },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left",
                        activeSection === item.id
                          ? "bg-blue-50 text-blue-600 font-medium"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                    >
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </nav>

                {/* Steps in sidebar */}
                {steps.length > 0 && (
                  <>
                    <Separator className="my-2" />
                    <div className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Steps</div>
                    <nav className="space-y-1">
                      {steps.map((step) => (
                        <button
                          key={step.id}
                          onClick={() => scrollToSection(`step-${step.id}`)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors text-left text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        >
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                            {step.number}
                          </span>
                          <span className="truncate">{step.title || `Step ${step.number}`}</span>
                        </button>
                      ))}
                    </nav>
                  </>
                )}
              </div>
            </div>

            {/* Center Content */}
            <div className="lg:col-span-7 space-y-6">
              {/* Info Bar */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Department</div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Building2 size={14} className="text-gray-400" />
                        {department ? `${department.departmentCode} - ${department.departmentName}` : "Unknown"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Owner</div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <UserIcon size={14} className="text-gray-400" />
                        {owner ? owner.name : "Unknown"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Cadence</div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Clock size={14} className="text-gray-400" />
                        {procedure.procedureCadence}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Created On</div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar size={14} className="text-gray-400" />
                        {toSydneyDate(procedure.createdAt)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Created By</div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <UserIcon size={14} className="text-gray-400" />
                        {creator ? creator.name : "Unknown"}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What Section */}
              <div id="section-what" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <BookOpen size={16} className="text-blue-600" />
                    </div>
                    <h2 className="text-lg font-semibold">What is this procedure</h2>
                  </div>
                  {!procedure.archived && !editingDescription && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_DESCRIPTION}>
                      <Button variant="ghost" size="icon" onClick={() => setEditingDescription(true)}>
                        <Pencil size={14} />
                      </Button>
                    </Can>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">Explain what this procedure is about in simple terms.</p>
                {editingDescription ? (
                  <div className="space-y-2">
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[120px] resize-none" placeholder="Enter a description..." />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingDescription(false); setDescription(procedure.description || ""); }}>Cancel</Button>
                      <Button size="sm" onClick={handleDescriptionSave}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    {procedure.description ? <p className="whitespace-pre-wrap">{procedure.description}</p> : <p className="text-muted-foreground italic">No description provided.</p>}
                  </div>
                )}
                <Separator className="mt-6" />
              </div>

              {/* Why Section */}
              <div id="section-why" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <HelpCircle size={16} className="text-amber-600" />
                    </div>
                    <h2 className="text-lg font-semibold">Why is this procedure important</h2>
                  </div>
                  {!procedure.archived && !editingWhyImportant && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_IMPORTANCE}>
                      <Button variant="ghost" size="icon" onClick={() => setEditingWhyImportant(true)}>
                        <Pencil size={14} />
                      </Button>
                    </Can>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">Help your team understand the value. What risk does it reduce?</p>
                {editingWhyImportant ? (
                  <div className="space-y-2">
                    <Textarea value={whyImportant} onChange={(e) => setWhyImportant(e.target.value)} className="min-h-[120px] resize-none" placeholder="Explain why this procedure is important..." />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingWhyImportant(false); setWhyImportant(procedure.whyImportant || ""); }}>Cancel</Button>
                      <Button size="sm" onClick={handleWhyImportantSave}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    {procedure.whyImportant ? <p className="whitespace-pre-wrap">{procedure.whyImportant}</p> : <p className="text-muted-foreground italic">No importance description provided.</p>}
                  </div>
                )}
                <Separator className="mt-6" />
              </div>

              {/* How Section - Step Builder */}
              <div id="section-how" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                      <CheckCircle size={16} className="text-green-600" />
                    </div>
                    <h2 className="text-lg font-semibold">How to complete this procedure</h2>
                  </div>
                  {!procedure.archived && !editingHowTo && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_INSTRUCTIONS}>
                      <Button variant="ghost" size="icon" onClick={startEditingHowTo}>
                        <Pencil size={14} />
                      </Button>
                    </Can>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">Step-by-step guide — each step can include text, videos, images, and links.</p>

                {editingHowTo ? (
                  <div className="space-y-4">
                    {stepsDraft.map((step, index) => (
                      <Card key={step.id} className="border border-gray-200">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-medium">
                              {step.number}
                            </span>
                            <Input
                              value={step.title}
                              onChange={(e) => updateStep(step.id, 'title', e.target.value)}
                              placeholder={`Step ${step.number} title`}
                              className="flex-1"
                              disabled={isSavingHowTo}
                            />
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveStepUp(index)} disabled={index === 0 || isSavingHowTo}>
                                <ChevronRight className="w-4 h-4 rotate-[-90deg]" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveStepDown(index)} disabled={index === stepsDraft.length - 1 || isSavingHowTo}>
                                <ChevronRight className="w-4 h-4 rotate-90" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeStep(step.id)} disabled={isSavingHowTo}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <CustomEditor
                            value={step.content}
                            onChange={(value) => updateStep(step.id, 'content', value)}
                            placeholder="Describe this step..."
                            className="min-h-[200px]"
                          />
                        </CardContent>
                      </Card>
                    ))}
                    <Button variant="outline" onClick={addStep} className="w-full" disabled={isSavingHowTo}>
                      <Plus size={16} className="mr-2" /> Add Step
                    </Button>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setEditingHowTo(false); setStepsDraft([...steps]); }} disabled={isSavingHowTo}>Cancel</Button>
                      <Button size="sm" onClick={handleHowToSave} disabled={isSavingHowTo}>{isSavingHowTo ? 'Saving...' : 'Save'}</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {steps.length > 0 ? steps.map((step) => (
                      <div key={step.id} id={`section-step-${step.id}`} className="scroll-mt-20">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-medium mt-0.5">
                            {step.number}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-medium text-sm">{step.title || `Step ${step.number}`}</h3>
                            <div 
                              className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: step.content || '<p></p>' }}
                            />
                          </div>
                        </div>
                      </div>
                    )) : (
                      <p className="text-muted-foreground italic text-sm">No steps added yet. Click the pencil icon to add steps.</p>
                    )}
                  </div>
                )}
                <Separator className="mt-6" />
              </div>

              {/* Systems Used */}
              <div id="section-systems" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                      <Layers size={16} className="text-purple-600" />
                    </div>
                    <h2 className="text-lg font-semibold">Systems used</h2>
                  </div>
                  {!procedure.archived && !editSystems && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_SYSTEMS}>
                      <Button variant="ghost" size="icon" onClick={() => setEditSystems(true)}>
                        <Pencil size={14} />
                      </Button>
                    </Can>
                  )}
                </div>
                {editSystems ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {allSystemsFormatted.map((sys) => (
                        <button
                          key={sys.value}
                          onClick={() => {
                            setSelectedSystems(prev =>
                              prev.includes(sys.value) ? prev.filter(s => s !== sys.value) : [...prev, sys.value]
                            );
                          }}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-sm border transition-colors",
                            selectedSystems.includes(sys.value)
                              ? "bg-blue-50 border-blue-200 text-blue-700"
                              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          {sys.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditSystems(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSystemsUpdate} disabled={isSavingSystems}>{isSavingSystems ? 'Saving...' : 'Save'}</Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {procedure.systems && procedure.systems.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {!isArrayOfStrings(procedure.systems) && procedure.systems.map((s: System, i: number) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1"><Monitor size={12} className="mr-1" />{s.name}</Badge>
                        ))}
                      </div>
                    ) : <p className="text-muted-foreground italic text-sm">No systems selected.</p>}
                  </div>
                )}
                <Separator className="mt-6" />
              </div>

              {/* Links */}
              <div id="section-links" className="scroll-mt-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <Link2 size={16} className="text-indigo-600" />
                    </div>
                    <h2 className="text-lg font-semibold">Links & resources</h2>
                  </div>
                  {!procedure.archived && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_LINKS}>
                      <Dialog open={openAddLink} onOpenChange={setOpenAddLink}>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="icon"><Plus size={14} /></Button>
                        </DialogTrigger>
                        <DialogContent aria-describedby={undefined}>
                          <DialogHeader><DialogTitle>Add New Link</DialogTitle><DialogDescription>Add a URL reference related to this procedure.</DialogDescription></DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2"><Label>Title</Label><Input value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} placeholder="e.g. Company Wiki" /></div>
                            <div className="space-y-2"><Label>URL</Label><Input value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} placeholder="https://example.com" /></div>
                            <div className="space-y-2"><Label>Description (Optional)</Label><Textarea value={newLink.description} onChange={(e) => setNewLink({ ...newLink, description: e.target.value })} className="resize-none" rows={2} /></div>
                          </div>
                          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleAddLink}>Add Link</Button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </Can>
                  )}
                </div>
                {procedure.links && procedure.links.length > 0 ? (
                  <ul className="space-y-3">
                    {procedure.links.map((link: ProcedureLink) => (
                      <li key={link.id} className="flex items-start justify-between group">
                        <div className="space-y-1">
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline text-sm">
                            <Link2 size={14} /><span>{link.title}</span><ExternalLink size={12} className="text-muted-foreground" />
                          </a>
                          {link.description && <p className="text-sm text-muted-foreground ml-6">{link.description}</p>}
                        </div>
                        {!procedure.archived && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Dialog open={openEditLink && editingLink?.id === link.id} onOpenChange={(open) => { setOpenEditLink(open); if (open) setEditingLink(link); }}>
                              <DialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><Pencil size={14} className="text-muted-foreground" /></Button></DialogTrigger>
                              <DialogContent aria-describedby={undefined}>
                                <DialogHeader><DialogTitle>Edit Link</DialogTitle></DialogHeader>
                                {editingLink && (
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2"><Label>Title</Label><Input value={editingLink.title} onChange={(e) => setEditingLink({ ...editingLink, title: e.target.value })} /></div>
                                    <div className="space-y-2"><Label>URL</Label><Input value={editingLink.url} onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })} /></div>
                                    <div className="space-y-2"><Label>Description</Label><Textarea value={editingLink.description || ""} onChange={(e) => setEditingLink({ ...editingLink, description: e.target.value })} className="resize-none" rows={2} /></div>
                                  </div>
                                )}
                                <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleEditLink}>Save</Button></DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleRemoveLink(link.id)}><Trash2 size={14} /></Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-muted-foreground italic text-sm">No links added yet.</p>}
                <Separator className="mt-6" />
              </div>

              {/* Video - hidden for now */}
              {/* {procedure.videoEmbed && (
                <div id="section-video" className="scroll-mt-20">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                      <Video size={16} className="text-red-600" />
                    </div>
                    <h2 className="text-lg font-semibold">Procedure video</h2>
                  </div>
                  <SharePointVideoPlayer videoEmbed={procedure.videoEmbed} title="Procedure Video" />
                </div>
              )} */}

              {/* Attachments */}
              <div id="section-attachments" className="scroll-mt-20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <FileText size={16} className="text-orange-600" />
                  </div>
                  <h2 className="text-lg font-semibold">Attachments</h2>
                </div>
                <FileAttachments
                  resourceType="procedures"
                  resourceId={procedure.id}
                  attachments={procedureAttachments}
                  onAttachmentsChange={handleAttachmentsChange}
                  disabled={procedure.archived}
                />
              </div>

              {/* Bottom spacer to allow last section to scroll to top */}
              <div className="h-[50vh]" />

            </div>

            {/* Right Sidebar */}
            <div className="hidden lg:block lg:col-span-3">
              <div className="sticky top-4 space-y-4">
                {/* Stakeholders */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserIcon size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">Stakeholders</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">{stakeholders.length}</Badge>
                    </div>
                    {stakeholders.length > 0 ? (
                      <div className="space-y-2">
                        {stakeholders.map((s) => (
                          <Link key={s.id} to={`/people/${s.id}`} className="flex items-center gap-2 text-sm hover:underline">
                            <UserIcon size={14} className="text-gray-400" />{s.name}
                          </Link>
                        ))}
                      </div>
                    ) : <p className="text-sm text-muted-foreground">No stakeholders assigned.</p>}
                  </CardContent>
                </Card>

                {/* Viewers */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Monitor size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">Viewers</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">{viewers.length}</Badge>
                    </div>
                    {viewers.length > 0 ? (
                      <div className="space-y-2">
                        {viewers.map((v) => (
                          <Link key={v.id} to={`/people/${v.id}`} className="flex items-center gap-2 text-sm hover:underline">
                            <UserIcon size={14} className="text-gray-400" />{v.name}
                          </Link>
                        ))}
                      </div>
                    ) : <p className="text-sm text-muted-foreground">No viewers assigned.</p>}
                  </CardContent>
                </Card>

                {/* Document Info */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText size={16} className="text-gray-400" />
                      <span className="text-sm font-medium">Document info</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Revision</span>
                        <span className="font-medium">v{procedure.currentVersion || '1.0'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Status</span>
                        <Badge variant={procedure.archived ? "secondary" : "default"} className="text-xs">{procedure.archived ? 'Archived' : 'Active'}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">ID</span>
                        <span className="font-medium">#{String(procedure.id).slice(-4)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Tasks</h2>
                <p className="text-sm text-muted-foreground">All tasks linked to this procedure.</p>
              </div>
              {!procedure.archived && (
                <Can I={ACTIONS.CREATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
                  <Dialog open={openAddActivity} onOpenChange={(open) => { setOpenAddActivity(open); if (!open) { setNewActivityName(""); setRaci({}); } }}>
                    <DialogTrigger asChild><Button><Plus size={16} className="mr-2" />Add Task</Button></DialogTrigger>
                    <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add New Task</DialogTitle>
                        <DialogDescription>Create a new task for this procedure.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="taskName" className="flex items-center gap-1">
                            Task Name
                            <span className="text-black">*</span>
                          </Label>
                          <Input
                            id="taskName"
                            placeholder="Enter task name"
                            value={newActivityName}
                            onChange={(e) => setNewActivityName(e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="responsiblePerson" className="flex items-center gap-1">
                            Responsible Person (R)
                            <span className="text-black">*</span>
                          </Label>
                          <SearchableSelect
                            groups={[
                              {
                                heading: "Users",
                                options: departmentalUsers.map((user) => ({
                                  value: `User-${user.id}`,
                                  label: user.name,
                                  subtitle: user.jobRole,
                                })),
                              },
                              {
                                heading: "Job Roles",
                                options: jobRoles.map((role) => ({
                                  value: `JobRole-${role.id}`,
                                  label: `${role.title} (Role)`,
                                })),
                              },
                              {
                                heading: "External Partners",
                                options: externalPartners.map((partner: ExternalPartner) => ({
                                  value: `ExternalPartner-${partner.id}`,
                                  label: `${partner.name} (External)`,
                                })),
                              },
                            ]}
                            value={raci?.responsiblePerson ? `${raci.responsiblePerson.type}-${raci.responsiblePerson.id}` : undefined}
                            onChange={(value) => handleRaciSelect('responsiblePerson', value)}
                            placeholder="Select responsible person"
                            searchPlaceholder="Search users, roles, or partners..."
                            emptyMessage="No results found."
                            noneOption={{ value: 'user-none', label: 'None' }}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who will be responsible for this task
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="accountablePerson" className="flex items-center gap-1">
                            Accountable Person (A)
                            <span className="text-black">*</span>
                          </Label>
                          <SearchableSelect
                            groups={[
                              {
                                heading: "Users",
                                options: departmentalUsers.map((user) => ({
                                  value: `User-${user.id}`,
                                  label: user.name,
                                  subtitle: user.jobRole,
                                })),
                              },
                              {
                                heading: "Job Roles",
                                options: jobRoles.map((role) => ({
                                  value: `JobRole-${role.id}`,
                                  label: `${role.title} (Role)`,
                                })),
                              },
                              {
                                heading: "External Partners",
                                options: externalPartners.map((partner: ExternalPartner) => ({
                                  value: `ExternalPartner-${partner.id}`,
                                  label: `${partner.name} (External)`,
                                })),
                              },
                            ]}
                            value={raci?.accountablePerson ? `${raci.accountablePerson.type}-${raci.accountablePerson.id}` : undefined}
                            onChange={(value) => handleRaciSelect('accountablePerson', value)}
                            placeholder="Select accountable person"
                            searchPlaceholder="Search users, roles, or partners..."
                            emptyMessage="No results found."
                            noneOption={{ value: 'user-none', label: 'None' }}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who will be accountable for this task
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="consultedPerson">Consulted Person (C)</Label>
                          <MultiSelect
                            options={[
                              ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                              ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                              ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                            ]}
                            selected={(raci?.consultedPerson ?? []).map(p => `${p.type}-${p.id}`)}
                            onChange={(selectedValues: string[]) => handleRaciSelect('consultedPerson', selectedValues)}
                            placeholder="Select consulted parties"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who should be consulted for this task
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="informedPerson">Informed Person (I)</Label>
                          <MultiSelect
                            options={[
                              ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                              ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                              ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                            ]}
                            selected={(raci?.informedPerson ?? []).map(p => `${p.type}-${p.id}`)}
                            onChange={(selectedValues: string[]) => handleRaciSelect('informedPerson', selectedValues)}
                            placeholder="Select informed parties"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who should be informed about this task
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddActivity}>Add Task</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </Can>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 hover:bg-gray-50">
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({
                        column: 'activityName',
                        direction: prev.column === 'activityName' && prev.direction === 'asc' ? 'desc' : 'asc'
                      }))}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'activityName' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({ column: 'responsible', direction: prev.column === 'responsible' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    >
                      <div className="flex items-center gap-1">
                        Responsible
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'responsible' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({ column: 'accountable', direction: prev.column === 'accountable' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    >
                      <div className="flex items-center gap-1">
                        Accountable
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'accountable' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({ column: 'consulted', direction: prev.column === 'consulted' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    >
                      <div className="flex items-center gap-1">
                        Consulted
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'consulted' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({ column: 'informed', direction: prev.column === 'informed' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    >
                      <div className="flex items-center gap-1">
                        Informed
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'informed' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => setActivitySort(prev => ({ column: 'createdAt', direction: prev.column === 'createdAt' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                    >
                      <div className="flex items-center gap-1">
                        Created On
                        <ArrowUpDown size={14} className={cn("text-gray-400", activitySort.column === 'createdAt' && "text-gray-700")} />
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const validActivities = procedureActivities.filter((a): a is Activity => !!a && !!a.id);
                    const isSorted = !(activitySort.column === 'activityName' && activitySort.direction === 'asc');
                    const sorted = [...validActivities].map((a, originalIndex) => ({ ...a, originalIndex })).sort((a, b) => {
                      const { column, direction } = activitySort;
                      const dir = direction === 'asc' ? 1 : -1;
                      let valA: string, valB: string;
                      switch (column) {
                        case 'activityName':
                          return (a.originalIndex - b.originalIndex) * dir;
                        case 'responsible':
                          valA = a.raci?.responsiblePerson ? getPartyName(a.raci.responsiblePerson) : '';
                          valB = b.raci?.responsiblePerson ? getPartyName(b.raci.responsiblePerson) : '';
                          break;
                        case 'accountable':
                          valA = a.raci?.accountablePerson ? getPartyName(a.raci.accountablePerson) : '';
                          valB = b.raci?.accountablePerson ? getPartyName(b.raci.accountablePerson) : '';
                          break;
                        case 'consulted':
                          valA = (a.raci?.consultedPerson && a.raci.consultedPerson.length > 0)
                            ? getPartyName(a.raci.consultedPerson[0]).charAt(0).toUpperCase()
                            : '';
                          valB = (b.raci?.consultedPerson && b.raci.consultedPerson.length > 0)
                            ? getPartyName(b.raci.consultedPerson[0]).charAt(0).toUpperCase()
                            : '';
                          break;
                        case 'informed':
                          valA = (a.raci?.informedPerson && a.raci.informedPerson.length > 0)
                            ? getPartyName(a.raci.informedPerson[0]).charAt(0).toUpperCase()
                            : '';
                          valB = (b.raci?.informedPerson && b.raci.informedPerson.length > 0)
                            ? getPartyName(b.raci.informedPerson[0]).charAt(0).toUpperCase()
                            : '';
                          break;
                        case 'createdAt':
                          valA = a.createdAt || '';
                          valB = b.createdAt || '';
                          break;
                        default:
                          return 0;
                      }
                      return valA.localeCompare(valB) * dir;
                    });
                    return sorted.length > 0 ? (
                      sorted.map((activity, index) => (
                        <TableRow key={activity.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                                {activity.originalIndex + 1}
                              </span>
                              <Link to={`/activities/${activity.id}`} className="text-sm hover:underline">
                                {activity.activityName}
                              </Link>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {activity.raci?.responsiblePerson ? getPartyName(activity.raci.responsiblePerson) : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {activity.raci?.accountablePerson ? getPartyName(activity.raci.accountablePerson) : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {activity.raci?.consultedPerson && activity.raci.consultedPerson.length > 0
                              ? activity.raci.consultedPerson.map(p => getPartyName(p)).join(', ')
                              : 'None'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {activity.raci?.informedPerson && activity.raci.informedPerson.length > 0
                              ? activity.raci.informedPerson.map(p => getPartyName(p)).join(', ')
                              : 'None'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {toSydneyDate(activity.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            {!procedure.archived && (
                              <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
                                <div className="flex items-center justify-end gap-1">
                                  {!isSorted && activity.originalIndex > 0 && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveActivityUp(activity.originalIndex)}>
                                      <ChevronRight className="w-4 h-4 rotate-[-90deg]" />
                                    </Button>
                                  )}
                                  {!isSorted && activity.originalIndex < validActivities.length - 1 && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveActivityDown(activity.originalIndex)}>
                                      <ChevronRight className="w-4 h-4 rotate-90" />
                                    </Button>
                                  )}
                                  <Dialog open={openEditActivity && editingActivity?.id === activity.id} onOpenChange={(open) => { setOpenEditActivity(open); if (open) setEditingActivity({ id: activity.id, activityName: activity.activityName, raci: activity.raci }); }}>
                                    <DialogTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <Pencil size={14} />
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[90vh] overflow-y-auto">
                                      <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
                                      {editingActivity && (
                                        <div className="space-y-4 py-4">
                                          <div className="space-y-2">
                                            <Label htmlFor="editTaskName">Task Name</Label>
                                            <Input
                                              id="editTaskName"
                                              value={editingActivity.activityName}
                                              onChange={(e) => setEditingActivity({ ...editingActivity, activityName: e.target.value })}
                                            />
                                          </div>

                                          <div className="space-y-2">
                                            <Label htmlFor="editResponsiblePerson" className="flex items-center gap-1">
                                              Responsible Person (R)
                                              <span className="text-black">*</span>
                                            </Label>
                                            <SearchableSelect
                                              groups={[
                                                {
                                                  heading: "Users",
                                                  options: departmentalUsers.map((user) => ({
                                                    value: `User-${user.id}`,
                                                    label: user.name,
                                                    subtitle: user.jobRole,
                                                  })),
                                                },
                                                {
                                                  heading: "Job Roles",
                                                  options: jobRoles.map((role) => ({
                                                    value: `JobRole-${role.id}`,
                                                    label: `${role.title} (Role)`,
                                                  })),
                                                },
                                                {
                                                  heading: "External Partners",
                                                  options: externalPartners.map((partner: ExternalPartner) => ({
                                                    value: `ExternalPartner-${partner.id}`,
                                                    label: `${partner.name} (External)`,
                                                  })),
                                                },
                                              ]}
                                              value={raci?.responsiblePerson ? `${raci.responsiblePerson.type}-${raci.responsiblePerson.id}` : undefined}
                                              onChange={(value) => handleRaciSelect('responsiblePerson', value)}
                                              placeholder="Select responsible person"
                                              searchPlaceholder="Search users, roles, or partners..."
                                              emptyMessage="No results found."
                                              noneOption={{ value: 'user-none', label: 'None' }}
                                            />
                                          </div>

                                          <div className="space-y-2">
                                            <Label htmlFor="editAccountablePerson" className="flex items-center gap-1">
                                              Accountable Person (A)
                                              <span className="text-black">*</span>
                                            </Label>
                                            <SearchableSelect
                                              groups={[
                                                {
                                                  heading: "Users",
                                                  options: departmentalUsers.map((user) => ({
                                                    value: `User-${user.id}`,
                                                    label: user.name,
                                                    subtitle: user.jobRole,
                                                  })),
                                                },
                                                {
                                                  heading: "Job Roles",
                                                  options: jobRoles.map((role) => ({
                                                    value: `JobRole-${role.id}`,
                                                    label: `${role.title} (Role)`,
                                                  })),
                                                },
                                                {
                                                  heading: "External Partners",
                                                  options: externalPartners.map((partner: ExternalPartner) => ({
                                                    value: `ExternalPartner-${partner.id}`,
                                                    label: `${partner.name} (External)`,
                                                  })),
                                                },
                                              ]}
                                              value={raci?.accountablePerson ? `${raci.accountablePerson.type}-${raci.accountablePerson.id}` : undefined}
                                              onChange={(value) => handleRaciSelect('accountablePerson', value)}
                                              placeholder="Select accountable person"
                                              searchPlaceholder="Search users, roles, or partners..."
                                              emptyMessage="No results found."
                                              noneOption={{ value: 'user-none', label: 'None' }}
                                            />
                                          </div>

                                          <div className="space-y-2">
                                            <Label htmlFor="editConsultedPerson">Consulted Person (C)</Label>
                                            <MultiSelect
                                              options={[
                                                ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                                                ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                                                ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                                              ]}
                                              selected={(raci?.consultedPerson ?? []).map(p => `${p.type}-${p.id}`)}
                                              onChange={(selectedValues: string[]) => handleRaciSelect('consultedPerson', selectedValues)}
                                              placeholder="Select consulted parties"
                                            />
                                          </div>

                                          <div className="space-y-2">
                                            <Label htmlFor="editInformedPerson">Informed Person (I)</Label>
                                            <MultiSelect
                                              options={[
                                                ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                                                ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                                                ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                                              ]}
                                              selected={(raci?.informedPerson ?? []).map(p => `${p.type}-${p.id}`)}
                                              onChange={(selectedValues: string[]) => handleRaciSelect('informedPerson', selectedValues)}
                                              placeholder="Select informed parties"
                                            />
                                          </div>
                                        </div>
                                      )}
                                      <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={handleUpdateActivity}>Save Changes</Button></DialogFooter>
                                    </DialogContent>
                                  </Dialog>
                                  {canDelete && (
                                    <Dialog open={openDeleteActivity && deleteActivityId === activity.id} onOpenChange={(open) => { setOpenDeleteActivity(open); if (open) setDeleteActivityId(activity.id); }}>
                                      <DialogTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500">
                                          <Trash2 size={14} />
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent aria-describedby={undefined}>
                                        <DialogHeader><DialogTitle>Delete Task</DialogTitle></DialogHeader>
                                        <div className="py-4">
                                          <p>Are you sure you want to delete "{activity.activityName}"?</p>
                                          <p className="text-sm text-muted-foreground mt-2">This action cannot be undone.</p>
                                        </div>
                                        {!isDeletingActivity ? (
                                          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button variant="destructive" onClick={handleDeleteActivity}>Delete</Button></DialogFooter>
                                        ) : (
                                          <DialogFooter><Button variant="destructive" disabled>Deleting...</Button></DialogFooter>
                                        )}
                                      </DialogContent>
                                    </Dialog>
                                  )}
                                </div>
                              </Can>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center h-24 text-muted-foreground">
                          No tasks created yet.
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity_log">
          <ActivityLog logs={activityLogs || []} entityName="Procedure" users={users} currentUser={currentUser} />
        </TabsContent>
      </Tabs>

      <ProcedureQRModal isOpen={qrModalOpen} onClose={() => setQrModalOpen(false)} procedure={procedure} />
    </div>
  );
}
